import React from 'react';
import { Table, TableBody, TableCell, TableContainer, Typography, TableHead, TableRow, Paper, IconButton, CircularProgress, Box, TableSortLabel, TextField, InputAdornment, Button, FormControlLabel, Checkbox } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { GroupInterface } from '../types';
import ImportOptionsDialog from './ImportOptionsDialog';

interface GroupsTableProps {
  groups: GroupInterface[];
  loading: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onGroupClick: (group: GroupInterface) => void;
  onAdd: () => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (column: string) => void;
  onSearch?: (query: string) => void;
  onResetSearch?: () => void;
  initialSearchQuery?: string;
  onExport?: () => void;
  onImport?: (data: any[]) => Promise<void>;
  onDownloadTemplate?: () => void;
  showInactive?: boolean;
  onInactiveChange?: (showInactive: boolean) => void;
}

function GroupsTable({
  groups,
  loading,
  onEdit,
  onDelete,
  onGroupClick,
  onAdd,
  sortBy,
  sortOrder,
  onSortChange,
  onSearch,
  onResetSearch,
  initialSearchQuery = '',
  onExport,
  onImport,
  onDownloadTemplate,
  showInactive = false,
  onInactiveChange
}: GroupsTableProps): React.JSX.Element {
  const [localInput, setLocalInput] = React.useState(initialSearchQuery);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [importOptionsOpen, setImportOptionsOpen] = React.useState(false);

  React.useEffect(() => {
    setLocalInput(initialSearchQuery);
  }, [initialSearchQuery]);


  return (
    <TableContainer component={Paper}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="h6">קבוצות</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onExport && (
            <IconButton
              color="primary"
              onClick={onExport}
              title="ייצוא ל-CSV"
              sx={{
                backgroundColor: 'grey.300',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'grey.400' }
              }}
            >
              <DownloadIcon />
            </IconButton>
          )}
          {onImport && (
            <>
              <input
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const Papa = await import('papaparse');
                  Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    complete: async (results) => {
                      if (results.data) {
                        await onImport(results.data);
                      }
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }
                  });
                }}
              />
              <IconButton
                color="primary"
                onClick={() => {
                  if (onDownloadTemplate) {
                    setImportOptionsOpen(true);
                  } else {
                    fileInputRef.current?.click();
                  }
                }}
                title="ייבוא מ-CSV"
                sx={{
                  backgroundColor: 'grey.300',
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'grey.400' }
                }}
              >
                <UploadFileIcon />
              </IconButton>
              <ImportOptionsDialog
                open={importOptionsOpen}
                onClose={() => setImportOptionsOpen(false)}
                onImportClick={() => fileInputRef.current?.click()}
                onDownloadTemplateClick={() => onDownloadTemplate?.()}
                title="אפשרויות ייבוא קבוצות"
              />
            </>
          )}
          <IconButton
            color="primary"
            onClick={onAdd}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 2, alignItems: 'center' }}>
        <TextField
          label="חיפוש קבוצה"
          variant="outlined"
          size="small"
          value={localInput}
          onChange={(e) => setLocalInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') onSearch?.(localInput);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <Button variant="contained" onClick={() => onSearch?.(localInput)}>חיפוש</Button>
        {(localInput || initialSearchQuery) && (
          <IconButton onClick={() => { setLocalInput(''); onResetSearch?.(); }} title="נקה חיפוש">
            <ClearIcon />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={showInactive}
              onChange={(e) => onInactiveChange?.(e.target.checked)}
              color="primary"
            />
          }
          label="הצג לא פעילים"
        />
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortBy === 'id'}
                direction={sortBy === 'id' ? sortOrder : 'asc'}
                onClick={() => onSortChange?.('id')}
              >
                ID
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === 'name'}
                direction={sortBy === 'name' ? sortOrder : 'asc'}
                onClick={() => onSortChange?.('name')}
              >
                שם
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === 'order'}
                direction={sortBy === 'order' ? sortOrder : 'asc'}
                onClick={() => onSortChange?.('order')}
              >
                מיקום במחירון
              </TableSortLabel>
            </TableCell>
            <TableCell>סטטוס</TableCell>
            <TableCell>פעולות</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : groups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                לא נמצאו קבוצות
              </TableCell>
            </TableRow>
          ) : (
            groups.map((group) => (
              <TableRow
                key={group.id}
                sx={{
                  height: '60px',
                  opacity: group.active === false ? 0.6 : 1,
                  '& td': {
                    maxHeight: '60px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }
                }}
              >
                <TableCell>{group.id}</TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                    onClick={() => onGroupClick && onGroupClick(group)}
                  >
                    {group.name}
                  </Box>
                </TableCell>
                <TableCell>{group.order || 0}</TableCell>
                <TableCell>{group.active === false ? 'לא פעיל' : 'פעיל'}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => onEdit(group.id)}><EditIcon /></IconButton>
                  <IconButton color="error" onClick={() => onDelete(group.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default GroupsTable; 